import type { z } from 'zod';

export interface ClientToolModule<Name extends string = string, Output = unknown> {
  name: Name;
  isAvailable?: boolean;
  outputSchema: z.ZodType<Output>;
  execute: (input: unknown) => Promise<Output>;
  invocationPolicy?: {
    maxAttemptsPerUserTurn: number;
    limitResult: Output;
  };
}

export type ClientToolExecutionResult<Output> =
  | { status: 'succeeded'; output: Output }
  | { status: 'failed'; message: string };

export interface ClientToolFailure {
  category: 'InvalidOutput' | 'MissingHandler' | 'ToolExecutionError' | 'UnavailableTool';
  toolName: string;
}

export type ReportClientToolFailure = (failure: ClientToolFailure) => void;

/** Surfaced to the model in place of any failure detail, which must never leak to the assistant. */
export const CLIENT_TOOL_ERROR_MESSAGE = 'Unable to execute this tool.';

export function reportClientToolFailure(failure: ClientToolFailure): void {
  console.error(JSON.stringify({ event: 'ava_ai_client_tool_error', ...failure }));
}

export function createClientToolExecutor<Name extends string, Output>(
  clientTool: ClientToolModule<Name, Output>,
  reportFailure: ReportClientToolFailure = reportClientToolFailure
) {
  let attempts = 0;

  async function execute(input: unknown): Promise<ClientToolExecutionResult<Output>> {
    if (clientTool.isAvailable === false) {
      reportFailure({ category: 'UnavailableTool', toolName: clientTool.name });
      return { status: 'failed', message: CLIENT_TOOL_ERROR_MESSAGE };
    }

    let output: unknown;
    try {
      const invocationPolicy = clientTool.invocationPolicy;
      if (invocationPolicy != null && attempts >= invocationPolicy.maxAttemptsPerUserTurn) {
        output = invocationPolicy.limitResult;
      } else {
        attempts += 1;
        output = await clientTool.execute(input);
      }
    } catch {
      reportFailure({ category: 'ToolExecutionError', toolName: clientTool.name });
      return { status: 'failed', message: CLIENT_TOOL_ERROR_MESSAGE };
    }

    const parsedOutput = clientTool.outputSchema.safeParse(output);
    if (!parsedOutput.success) {
      reportFailure({ category: 'InvalidOutput', toolName: clientTool.name });
      return { status: 'failed', message: CLIENT_TOOL_ERROR_MESSAGE };
    }

    return { status: 'succeeded', output: parsedOutput.data };
  }

  return {
    execute,
    startUserTurn: () => {
      attempts = 0;
    },
  };
}
